import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LanguageDropdown } from '@/components/LanguageDropdown';
import { SignOutButton } from '@/components/SignOutButton';
import { APP } from '@/config/app';
import { useAuth } from '@/contexts/AuthContext';
import { useCmsDocument } from '@/hooks/useCmsDocument';
import { NAV_ROUTES, type NavKey } from '@/lib/cms/nav-routes';
import { findSection } from '@/lib/cms/section-types';

type NavLinkEntry = { id: string; key: NavKey; label: string };

// Shared header for PublicLayout, BuyerLayout, and AuthLayout. Sticky
// paper-coloured strip laid out as a 3-column grid: brand left, nav
// centred, actions right. Below 768px the desktop nav and actions are
// hidden via CSS; a hamburger trigger mounts an off-canvas drawer that
// hosts the same links + auth slot. The drawer is only mounted while
// open so existing `getByRole` queries that target nav links keep
// resolving to a single match.
export function PublicLayoutHeader() {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  const isAdminRole =
    auth.status === 'authenticated' &&
    (auth.profile.role === 'admin' || auth.profile.role === 'super_admin');
  const dashboardHref = isAdminRole ? '/admin' : '/dashboard';
  const dashboardLabel = isAdminRole ? t('nav.admin') : t('nav.dashboard');

  // CMS-driven primary nav (L6.5, strict): the admin reorders/relabels/hides
  // links on the `nav` slug. While the document loads, when the row/section is
  // absent or hidden, or for items hidden / left without a label, nothing is
  // rendered — no hardcoded fallback resurrects links the CMS does not provide.
  const navSection = findSection(useCmsDocument('nav'), 'nav_links');
  const navLinks: NavLinkEntry[] =
    navSection && navSection.visible
      ? navSection.items
          .filter((item) => item.visible && item.label.trim() !== '')
          .map((item) => ({ id: item.id, key: item.key, label: item.label }))
      : [];

  const renderAuthSlot = () => {
    if (auth.status === 'authenticated') {
      return (
        <>
          <Link to={dashboardHref} className="btn btn--ghost">
            {dashboardLabel}
          </Link>
          <SignOutButton variant="public" />
        </>
      );
    }
    if (auth.status === 'anonymous') {
      return (
        <>
          <Link to="/signin" className="btn btn--ghost">
            {t('nav.signIn')}
          </Link>
          <Link to="/sell" className="btn btn--primary">
            {t('nav.sell')}
          </Link>
        </>
      );
    }
    // Loading: render two placeholder spans matching the anonymous
    // ghost+primary slot to avoid the 1-2s anonymous-button flash on
    // reload while AuthContext fetches the profile. aria-hidden keeps
    // them out of the accessibility tree.
    return (
      <>
        <span className="btn btn--ghost btn--skeleton" aria-hidden="true" />
        <span className="btn btn--primary btn--skeleton" aria-hidden="true" />
      </>
    );
  };

  return (
    <header className="layout__header" role="banner">
      <div className="container layout__header-row">
        <Link to="/" className="brand" aria-label={APP.name}>
          <img src="/logo.svg" alt="" className="brand__logo" />
        </Link>

        <nav className="layout__nav" aria-label={t('nav.primary')}>
          {navLinks.map((link) => (
            <NavLink key={link.id} to={NAV_ROUTES[link.key]}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="layout__actions">
          <LanguageDropdown />
          {renderAuthSlot()}
        </div>

        <button
          type="button"
          className="layout__hamburger"
          aria-label={t('nav.openMenu')}
          aria-expanded={mobileNavOpen}
          aria-controls="public-mobile-nav"
          onClick={() => setMobileNavOpen((v) => !v)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {mobileNavOpen ? (
        <>
          <button
            type="button"
            className="layout__mobile-nav-backdrop"
            aria-label={t('nav.closeMenu')}
            onClick={() => setMobileNavOpen(false)}
          />
          <aside
            id="public-mobile-nav"
            className="layout__mobile-nav"
            aria-label={t('nav.primary')}
          >
            <div className="layout__mobile-nav-head">
              <img src="/logo.svg" alt="" aria-hidden="true" className="brand__logo" />
              <button
                type="button"
                className="layout__mobile-nav-close"
                aria-label={t('nav.closeMenu')}
                onClick={() => setMobileNavOpen(false)}
              >
                ×
              </button>
            </div>
            <nav className="layout__mobile-nav-links">
              {navLinks.map((link) => (
                <NavLink key={link.id} to={NAV_ROUTES[link.key]}>
                  {link.label}
                </NavLink>
              ))}
            </nav>
            <div className="layout__mobile-nav-actions">
              <LanguageDropdown />
              {renderAuthSlot()}
            </div>
          </aside>
        </>
      ) : null}
    </header>
  );
}
